import { useState } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowUpRight, Phone, CheckCircle2, Upload, X, Car, User, MessageSquare,
} from "lucide-react";
import { SITE, API_URL } from "@/lib/site";
import { fileToBase64 } from "@/lib/upload";
import { scrollToTop } from "@/lib/scroll";
import { Reveal, SectionHead } from "@/components/motion";
import { useSEO } from "@/components/SEO";

const TYPES_VEHICULE = ["Automobile", "Moto", "Scooter"];
const INTERVENTIONS = [
  "Entretien / vidange",
  "Freinage",
  "Diagnostic (voyant allumé)",
  "Mécanique générale",
  "Pneus",
  "Préparation à l'expertise",
  "Nettoyage & préparation esthétique (sur rendez-vous)",
  "Autre / je ne sais pas",
];

const INITIAL = {
  type_vehicule: "", marque: "", modele: "", annee: "", kilometrage: "",
  type_intervention: "", message: "", nom: "", telephone: "", email: "",
};

export default function Devis() {
  useSEO(
    "Demande de devis — Mécanicien Prilly | BM MECHANICS",
    "Demandez un devis (CHF 30.-, déduit des travaux si vous validez) pour l'entretien ou la réparation de votre voiture, moto ou scooter à Prilly, Ouest lausannois."
  );
  const location = useLocation();
  const [form, setForm] = useState(() => ({
    ...INITIAL,
    type_intervention: location.state?.intervention || "",
  }));
  const [photos, setPhotos] = useState([]);
  const [errors, setErrors] = useState({});
  const [sending, setSending] = useState(false);
  const [reference, setReference] = useState(null);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const onPhotos = async (e) => {
    const files = Array.from(e.target.files || []).slice(0, 3 - photos.length);
    if (!files.length) return;
    try {
      const encoded = await Promise.all(files.map(fileToBase64));
      setPhotos((p) => [...p, ...encoded].slice(0, 3));
      toast.success(`${files.length} photo(s) ajoutée(s)`);
    } catch {
      toast.error("Impossible de lire une photo");
    }
    e.target.value = "";
  };

  const validate = () => {
    const errs = {};
    if (!form.type_vehicule) errs.type_vehicule = "Sélectionnez un type de véhicule";
    if (!form.type_intervention) errs.type_intervention = "Sélectionnez un type d'intervention";
    if (!form.nom.trim()) errs.nom = "Votre nom est requis";
    if (!form.telephone.trim()) errs.telephone = "Votre téléphone est requis";
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) errs.email = "Email invalide";
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      toast.error("Veuillez compléter les champs requis");
      return;
    }
    setSending(true);
    try {
      const res = await fetch(`${API_URL}/devis`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, photos }),
      });
      if (!res.ok) throw new Error();
      const data = await res.json();
      setReference(data.reference);
      scrollToTop();
    } catch {
      toast.error("Échec de l'envoi. Appelez-nous directement au " + SITE.phoneDisplay);
    } finally {
      setSending(false);
    }
  };

  if (reference) {
    return (
      <div data-testid="devis-page" className="pt-32 pb-24 min-h-screen">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 text-center">
          <Reveal>
            <span className="inline-flex h-16 w-16 items-center justify-center bg-[#121212] text-[#8C7B5D] rounded-full">
              <CheckCircle2 className="h-8 w-8" />
            </span>
            <h1 className="mt-8 font-display text-3xl sm:text-4xl font-black uppercase tracking-tight text-[#121212]">
              Demande envoyée
            </h1>
            <p className="mt-4 font-mono text-xs uppercase tracking-[0.2em] text-[#8C7B5D]" data-testid="devis-reference">
              Référence : {reference}
            </p>
            <p className="mt-6 text-sm sm:text-base leading-relaxed text-[#4A4D53]">
              Merci {form.nom.split(" ")[0]}. Votre demande de devis a bien été transmise
              à l'atelier. Vous serez recontacté au <strong className="text-[#121212]">{form.telephone}</strong> dans
              les meilleurs délais.
            </p>
            <div className="mt-8 flex flex-wrap justify-center gap-3">
              <a
                href={SITE.phoneHref}
                data-testid="devis-confirm-call-btn"
                className="inline-flex items-center gap-2 bg-[#121212] text-[#F6F3EB] font-mono text-xs uppercase tracking-[0.16em] px-6 py-4 hover:bg-[#8C7B5D] hover:text-[#121212] transition-colors"
              >
                <Phone className="h-4 w-4" /> Urgent ? Appelez-nous
              </a>
              <button
                data-testid="devis-new-btn"
                onClick={() => { setReference(null); setForm(INITIAL); setPhotos([]); }}
                className="inline-flex items-center gap-2 border border-[#121212] text-[#121212] font-mono text-xs uppercase tracking-[0.16em] px-6 py-4 hover:bg-[#121212] hover:text-[#F6F3EB] transition-colors"
              >
                Nouvelle demande
              </button>
            </div>
          </Reveal>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="devis-page">
      <section className="bg-[#121212] text-[#F6F3EB] pt-32 pb-16 md:pt-40 md:pb-20 texture-grain">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#8C7B5D]">Devis</p>
            <h1 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-black uppercase tracking-tight leading-[0.95]">
              Demande de devis
            </h1>
            <p className="mt-5 max-w-2xl text-sm sm:text-base leading-relaxed text-[#9A968C]">
              Deux minutes suffisent. Décrivez votre véhicule et votre besoin :
              l'atelier vous rappelle avec une estimation claire. Le devis coûte
              CHF 30.-, intégralement déduits de la facture si vous validez les travaux.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="py-12 md:py-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <form onSubmit={submit} data-testid="devis-form" noValidate>
            {/* Véhicule */}
            <Reveal>
              <div className="bg-white border border-[#D8D4CA] p-6 sm:p-8">
                <div className="flex items-center gap-3 mb-6">
                  <Car className="h-5 w-5 text-[#8C7B5D]" />
                  <h2 className="font-display text-lg font-bold uppercase tracking-wide">Votre véhicule</h2>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label htmlFor="type_vehicule" className="bm-label">Type de véhicule *</label>
                    <select
                      id="type_vehicule"
                      data-testid="devis-vehicle-type-select"
                      className="bm-input"
                      value={form.type_vehicule}
                      onChange={set("type_vehicule")}
                    >
                      <option value="">Sélectionner…</option>
                      {TYPES_VEHICULE.map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>
                    {errors.type_vehicule && <p data-testid="devis-error-vehicle" className="mt-1.5 text-xs text-[#A33E2B]">{errors.type_vehicule}</p>}
                  </div>
                  <div>
                    <label htmlFor="marque" className="bm-label">Marque</label>
                    <input id="marque" data-testid="devis-brand-input" className="bm-input" placeholder="Ex. Peugeot, Yamaha, Piaggio" value={form.marque} onChange={set("marque")} />
                  </div>
                  <div>
                    <label htmlFor="modele" className="bm-label">Modèle</label>
                    <input id="modele" data-testid="devis-model-input" className="bm-input" placeholder="Ex. 208, MT-07, Vespa GTS" value={form.modele} onChange={set("modele")} />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="annee" className="bm-label">Année</label>
                      <input id="annee" data-testid="devis-year-input" className="bm-input" placeholder="2019" inputMode="numeric" value={form.annee} onChange={set("annee")} />
                    </div>
                    <div>
                      <label htmlFor="kilometrage" className="bm-label">Kilométrage</label>
                      <input id="kilometrage" data-testid="devis-km-input" className="bm-input" placeholder="85 000" inputMode="numeric" value={form.kilometrage} onChange={set("kilometrage")} />
                    </div>
                  </div>
                </div>
              </div>
            </Reveal>

            {/* Intervention */}
            <Reveal delay={0.05}>
              <div className="bg-white border border-[#D8D4CA] border-t-0 p-6 sm:p-8">
                <div className="flex items-center gap-3 mb-6">
                  <MessageSquare className="h-5 w-5 text-[#8C7B5D]" />
                  <h2 className="font-display text-lg font-bold uppercase tracking-wide">Votre besoin</h2>
                </div>
                <div>
                  <label htmlFor="type_intervention" className="bm-label">Type d'intervention *</label>
                  <select
                    id="type_intervention"
                    data-testid="devis-intervention-select"
                    className="bm-input"
                    value={form.type_intervention}
                    onChange={set("type_intervention")}
                  >
                    <option value="">Sélectionner…</option>
                    {INTERVENTIONS.map((i) => <option key={i} value={i}>{i}</option>)}
                  </select>
                  {errors.type_intervention && <p data-testid="devis-error-intervention" className="mt-1.5 text-xs text-[#A33E2B]">{errors.type_intervention}</p>}
                </div>
                <div className="mt-5">
                  <label htmlFor="message" className="bm-label">Décrivez le problème ou le besoin</label>
                  <textarea
                    id="message"
                    data-testid="devis-message-input"
                    className="bm-input min-h-[120px] resize-y"
                    placeholder="Ex. bruit au freinage à l'avant, voyant moteur allumé depuis une semaine…"
                    value={form.message}
                    onChange={set("message")}
                  />
                </div>
                <div className="mt-5">
                  <span className="bm-label">Photos (facultatif, max 3)</span>
                  <label
                    data-testid="devis-photo-upload"
                    className="flex flex-col items-center justify-center gap-2 border border-dashed border-[#B7B2A4] bg-[#F6F3EB] px-6 py-8 cursor-pointer hover:border-[#121212] transition-colors"
                  >
                    <Upload className="h-5 w-5 text-[#8C7B5D]" />
                    <span className="font-mono text-[11px] uppercase tracking-[0.16em] text-[#5A5D64]">
                      Ajouter une photo du véhicule
                    </span>
                    <input type="file" accept="image/*" multiple className="hidden" onChange={onPhotos} data-testid="devis-photo-input" />
                  </label>
                  {photos.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-3" data-testid="devis-photo-preview">
                      {photos.map((p, i) => (
                        <div key={i} className="relative border border-[#D8D4CA] bg-white p-1">
                          <img src={p} alt={`Photo ${i + 1}`} className="h-20 w-20 object-cover" />
                          <button
                            type="button"
                            data-testid={`devis-photo-remove-${i}`}
                            aria-label={`Supprimer la photo ${i + 1}`}
                            onClick={() => setPhotos((arr) => arr.filter((_, j) => j !== i))}
                            className="absolute -top-2 -right-2 h-6 w-6 bg-[#121212] text-[#F6F3EB] flex items-center justify-center"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </Reveal>

            {/* Contact */}
            <Reveal delay={0.1}>
              <div className="bg-white border border-[#D8D4CA] border-t-0 p-6 sm:p-8">
                <div className="flex items-center gap-3 mb-6">
                  <User className="h-5 w-5 text-[#8C7B5D]" />
                  <h2 className="font-display text-lg font-bold uppercase tracking-wide">Vos coordonnées</h2>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label htmlFor="nom" className="bm-label">Nom & prénom *</label>
                    <input id="nom" data-testid="devis-name-input" className="bm-input" placeholder="Votre nom" value={form.nom} onChange={set("nom")} />
                    {errors.nom && <p data-testid="devis-error-name" className="mt-1.5 text-xs text-[#A33E2B]">{errors.nom}</p>}
                  </div>
                  <div>
                    <label htmlFor="telephone" className="bm-label">Téléphone *</label>
                    <input id="telephone" data-testid="devis-phone-input" className="bm-input" placeholder="07x xxx xx xx" inputMode="tel" value={form.telephone} onChange={set("telephone")} />
                    {errors.telephone && <p data-testid="devis-error-phone" className="mt-1.5 text-xs text-[#A33E2B]">{errors.telephone}</p>}
                  </div>
                  <div className="sm:col-span-2">
                    <label htmlFor="email" className="bm-label">Email (facultatif)</label>
                    <input id="email" data-testid="devis-email-input" type="email" className="bm-input" placeholder="vous@exemple.ch" value={form.email} onChange={set("email")} />
                    {errors.email && <p data-testid="devis-error-email" className="mt-1.5 text-xs text-[#A33E2B]">{errors.email}</p>}
                  </div>
                </div>
                <button
                  type="submit"
                  data-testid="devis-submit-btn"
                  disabled={sending}
                  className="mt-8 w-full inline-flex items-center justify-center gap-2 bg-[#121212] text-[#F6F3EB] font-mono text-xs uppercase tracking-[0.18em] px-6 py-4 hover:bg-[#8C7B5D] hover:text-[#121212] transition-colors disabled:opacity-50 disabled:pointer-events-none"
                >
                  {sending ? "Envoi en cours…" : "Envoyer ma demande de devis"} <ArrowUpRight className="h-4 w-4" />
                </button>
                <p className="mt-4 text-center font-mono text-[10px] uppercase tracking-[0.16em] text-[#9A968C]">
                  CHF 30.- · Déduits des travaux si vous validez · Réponse rapide
                </p>
              </div>
            </Reveal>
          </form>

          <Reveal delay={0.15}>
            <div className="mt-6 border border-[#D8D4CA] bg-[#EFECE4] p-6 flex flex-wrap items-center justify-between gap-4">
              <p className="text-sm text-[#4A4D53]">
                Vous préférez en parler de vive voix ?
              </p>
              <a
                href={SITE.phoneHref}
                data-testid="devis-call-btn"
                className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.16em] text-[#121212] border-b-2 border-[#121212] pb-1 hover:text-[#8C7B5D] hover:border-[#8C7B5D] transition-colors"
              >
                <Phone className="h-4 w-4" /> {SITE.phoneDisplay}
              </a>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
